import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { BarChart, Bar, XAxis, ResponsiveContainer } from 'recharts'
import MobileCard from '../../components/mobile/mobileCard'
import { authHeader } from '../../functions/_helpers'

const periods = [
   { value: 'daily', label: 'Dag' },
   { value: 'weekly', label: 'Uke' },
   { value: 'monthly', label: 'Måned' }
]

const PeriodSelect = ({ value, onChange }) => (
   <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="text-sm md:text-base text-gray-500 bg-transparent outline-none">
      {periods.map(p => (
         <option key={p.value} value={p.value}>
            {p.label}
         </option>
      ))}
   </select>
)

// Sums a per-entity (permit holder/driver) statistics dictionary down to a single
// {daily, weekly, monthly} object, matching the same reduction used by the desktop
// dashboard (components/dashboard/dashboards.js). For a driver there is normally only
// one entry, but this keeps the same shape/behaviour as the rest of the app.
const sumStatisticProperty = (data, property) => {
   const result = { daily: 0, weekly: 0, monthly: 0 }
   if (!data) return result
   Object.values(data).forEach(item => {
      const details = item && item[property]
      if (!details) return
      result.daily += details.daily || 0
      result.weekly += details.weekly || 0
      result.monthly += details.monthly || 0
   })
   return result
}

const sumRevenue = data => {
   const result = { daily: 0, weekly: 0, monthly: 0 }
   if (!data) return result
   Object.values(data).forEach(item => {
      const total = item && item.total
      if (!total) return
      result.daily += total.daily || 0
      result.weekly += total.weekly || 0
      result.monthly += total.monthly || 0
   })
   return result
}

// Combines the (usually single) per-driver trend list into one array of
// {completed, cancelled, booked, revenue, sampleDate}, indexed by day.
const combineTrendData = data => {
   if (!data) return []
   const lists = Object.values(data)
   const firstList = lists[0]
   if (!firstList) return []

   return firstList.map((_, index) => {
      const day = { completed: 0, cancelled: 0, booked: 0, revenue: 0, sampleDate: null }
      lists.forEach(list => {
         const entry = list && list[index]
         if (!entry) return
         day.completed += entry.completed || 0
         day.cancelled += entry.cancelled || 0
         day.booked += entry.booked || 0
         day.revenue += entry.revenue || 0
         day.sampleDate = day.sampleDate || entry.sampleDate
      })
      return day
   })
}

const MobileDashboard = () => {
   const [tripPeriod, setTripPeriod] = useState('daily')
   const [revenuePeriod, setRevenuePeriod] = useState('daily')
   const [loading, setLoading] = useState(true)
   const [tripStatistics, setTripStatistics] = useState({ active: 0, completed: 0, cancelled: 0, bookings: 0 })
   const [revenue, setRevenue] = useState({ daily: 0, weekly: 0, monthly: 0 })
   const [tripTrend, setTripTrend] = useState([])

   useEffect(() => {
      const apiUrl = process.env.REACT_APP_API_URL

      const fetchData = async () => {
         setLoading(true)
         try {
            const [tripStatsRes, revenueRes, trendRes] = await Promise.all([
               axios.get(apiUrl + '/api/dashboard/tripStatistics', { headers: authHeader() }),
               axios.get(apiUrl + '/api/dashboard/revenueStatistics', { headers: authHeader() }),
               axios.get(apiUrl + '/api/dashboard/tripTrendData/30', { headers: authHeader() })
            ])

            setTripStatistics({
               active: sumStatisticProperty(tripStatsRes.data, 'active'),
               completed: sumStatisticProperty(tripStatsRes.data, 'completed'),
               cancelled: sumStatisticProperty(tripStatsRes.data, 'cancelled'),
               bookings: sumStatisticProperty(tripStatsRes.data, 'bookings')
            })
            setRevenue(sumRevenue(revenueRes.data))
            setTripTrend(combineTrendData(trendRes.data))
         } catch (error) {
            console.error('Error fetching mobile dashboard data:', error)
         } finally {
            setLoading(false)
         }
      }

      fetchData()
   }, [])

   if (loading) {
      return <div className="p-4 text-center text-gray-500 md:text-lg">Laster...</div>
   }

   return (
      <div className="p-4 space-y-4 md:p-6 md:space-y-6">
         {/* On tablet-sized viewports (md+) these two cards sit side by side
             to make better use of the extra width; the trend card below
             stays full width. */}
         <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-6">
            <MobileCard>
               <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg md:text-xl font-semibold text-gray-800">Aktive turer</h2>
                  <PeriodSelect value={tripPeriod} onChange={setTripPeriod} />
               </div>
               <div className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">{tripStatistics.active[tripPeriod]}</div>
               <div className="grid grid-cols-3 gap-2 text-sm md:text-base">
                  <div>
                     <div className="text-gray-800 font-semibold">{tripStatistics.completed[tripPeriod]}</div>
                     <div className="text-xs md:text-sm text-gray-500">Fullførte</div>
                  </div>
                  <div>
                     <div className="text-gray-800 font-semibold">{tripStatistics.cancelled[tripPeriod]}</div>
                     <div className="text-xs md:text-sm text-gray-500">Kansellerte</div>
                  </div>
                  <div>
                     <div className="text-gray-800 font-semibold">{tripStatistics.bookings[tripPeriod]}</div>
                     <div className="text-xs md:text-sm text-gray-500">Forespørsler</div>
                  </div>
               </div>
            </MobileCard>

            <MobileCard>
               <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg md:text-xl font-semibold text-gray-800">Omsetning</h2>
                  <PeriodSelect value={revenuePeriod} onChange={setRevenuePeriod} />
               </div>
               <div className="text-3xl md:text-4xl font-bold text-gray-800">{Math.round(revenue[revenuePeriod] / 100)} kr</div>
            </MobileCard>
         </div>

         <MobileCard>
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">Siste 30 dager</h2>
            <div style={{ width: '100%', height: 160 }}>
               <ResponsiveContainer>
                  <BarChart data={tripTrend}>
                     <XAxis dataKey="sampleDate" hide />
                     <Bar dataKey="revenue" fill="#60a5fa" radius={[4, 4, 0, 0]} />
                  </BarChart>
               </ResponsiveContainer>
            </div>
         </MobileCard>
      </div>
   )
}

export default MobileDashboard
